import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus } from 'react-icons/fa';
import { Input, Modal, ModalHeader, ModalBody, ModalFooter, Badge } from 'reactstrap'
import './Announcement.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getUserInfo, getUserRolePermissions } from '../../services/userServicer';
import { getAnnouncement, createAnnouncement, uploadFile } from '../../services/announcementServices';
import Select from 'react-select';
import { getInstitutions } from '../../services/institutions';
import { FiFile, FiSearch } from "react-icons/fi";

import LoadingScreen from 'src/Loading/LoadingScreen';
import { useSearchQuery } from 'src/hook/useSearchQuery';
import authService from 'src/services/authService';
import LoadingButton from '@/components/Buttons/LoadingButton';


type LoadingMode = 'global' | 'table';

/** Role API: one object per `page` with flags on the same object (no nested `.permissions`). */
const normalizeAnnouncementPage = (value: unknown) => String(value ?? '').trim().toLowerCase();
const permissionToBool = (value: unknown): boolean => {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }
  return Boolean(value);
};

export default function AnnouncementSup() {
const {t}=useTranslation();
const [currentPage, setCurrentPage] = useState(0);
const [perPage, setPerPage] = useState<number>(10);
const [searchTerm, setSearchTerm] = useState("");
const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
const [data, setData] = useState<any[]>([]);
const [totalPages, setTotalPages] = useState<number>(0);
const navigate = useNavigate();
const [showPublishModal, setShowPublishModal] = useState(false);
const [rowId, setRowId] = useState<string>("");
const [rowStatus, setRowStatus] = useState<string>("");
const [announcementMessage, setAnnouncementMessage] = useState("");
const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
const [announcementSubject, setAnnouncementSubject] = useState("");
const [tenants, setTenants] = useState<string[]>(["Tenant 1", "Tenant 2", "Tenant 3"]); // Example tenants
const [uploadedFile, setUploadedFile] = useState<any>(null);
const [permissions, setPermissions] = useState<any>(null);
const [notAllowed, setNotAllowed] = useState(false);
const [permissionsChecked, setPermissionsChecked] = useState(false);
const [fileInputKey, setFileInputKey] = useState(0);
const [savingAnnouncement, setSavingAnnouncement] = useState(false);

const customSelectStyles = {
  control: (provided: any) => ({
      ...provided,
      minHeight: '40px', // Reduce the height
  }),
  valueContainer: (provided: any) => ({
      ...provided,
      height: '40px',
      padding: '0 6px',
  }),
  input: (provided: any) => ({
      ...provided,
      margin: '0px',
  }),
  indicatorsContainer: (provided: any) => ({
      ...provided,
      height: '40px',
  }),
};
const [sortConfig, setSortConfig] = useState<{
  key: keyof any | "index";
  direction: "ascending" | "descending" | null;
}>({ key: "index", direction: "ascending" });


const [institutions, setInstitutions] = useState<any[]>([]);
const [globalLoading, setGlobalLoading] = useState<boolean>(true);
const [tableLoading, setTableLoading] = useState<boolean>(false);
const didFirstLoad = useRef<boolean>(false);

// Use the search query hook
const { searchQuery, searchResultType, clearSearchQuery } = useSearchQuery();

const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const files = event.target.files;
  if (files) {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    setUploadedFile(formData)
  }
};

const fetchAnnouncementData = async (page: number, query: string, loadingMode: LoadingMode = 'table') => {
  if (loadingMode === 'global') {
    setGlobalLoading(true);
  } else {
    setTableLoading(true);
  }
  try {
    const response: any = await getAnnouncement(query, perPage, page);   
    if (response && response.success) {
      const indexedData = response.data.map((user: any, idx: number) => ({
        ...user,
        index: idx + 1 + page * perPage,
      }));
      setData(indexedData);
      setTotalPages(Math.ceil(response.totalCount / perPage));
    } else if (response && response.data.length === 0) {
      setData([]);
      setTotalPages(0);
    } else {
      toast.error(t("announcement.failed_to_fetch_announcement_data"));
    }
  } catch (error: any) {
    toast.error(t("announcement.error_fetching_announcement_data")+":", error);
  } finally {
    if (loadingMode === 'global') {
      setGlobalLoading(false);
    } else {
      setTableLoading(false);
    }
  }
};

const fetchInstitutions = async () => {
  try {
    const response = await getInstitutions("", 1000, 0);
    if (response.success) {
      setInstitutions(response.data);
    }
  } catch (error: any) {
    toast.error("Error fetching institutions data:", error);
  }
};

useEffect(() => {
  const fetchPermissions = async () => {
    let allowed = false;
    try {
      const user = await getUserInfo();
      if (!user?.id) {
        setPermissions(null);
        setNotAllowed(true);
        setPermissionsChecked(true);
        setGlobalLoading(false);
        return;
      }

      const response = await getUserRolePermissions(user.id);
      if (
        response?.success &&
        response?.data?.permissions &&
        Array.isArray(response.data.permissions)
      ) {
        const pagePermission = response.data.permissions.find(
          (p: { page?: string }) => normalizeAnnouncementPage(p?.page) === 'announcement'
        );
        if (pagePermission) {
          setPermissions(pagePermission);
          allowed = permissionToBool(pagePermission.show);
          setNotAllowed(!allowed);
        } else {
          setPermissions(null);
          allowed = false;
          setNotAllowed(true);
        }
      } else {
        setPermissions(null);
        allowed = false;
        setNotAllowed(true);
      }
    } catch {
      allowed = false;
      setNotAllowed(true);
      setPermissions(null);
    } finally {
      setPermissionsChecked(true);
      if (!allowed) {
        setGlobalLoading(false);
      }
    }
  };

  fetchPermissions();
}, []);

// Debounce search input by 1s
useEffect(() => {
  const handle = setTimeout(() => {
    setDebouncedSearchTerm(searchTerm);
  }, 1000);
  return () => clearTimeout(handle);
}, [searchTerm]);

// Trigger backend fetch on page/perPage or debounced search changes
useEffect(() => {
  if (!permissionsChecked || !permissionToBool(permissions?.show)) return;
  if (!didFirstLoad.current) {
    didFirstLoad.current = true;
    fetchAnnouncementData(currentPage, debouncedSearchTerm, 'global');
  } else {
    fetchAnnouncementData(currentPage, debouncedSearchTerm, 'table');
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [currentPage, perPage, debouncedSearchTerm, permissionsChecked, permissions]);

// Fetch institutions only when allowed
useEffect(() => {
  if (!permissionsChecked || !permissionToBool(permissions?.show)) return;
  fetchInstitutions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [permissionsChecked, permissions]);

const sortedData = useMemo(() => {
  let sortableData = [...data];
  if (sortConfig.direction !== null) {
    sortableData.sort((a: any, b: any) => {
      if (sortConfig.key === "index") {
        return sortConfig.direction === "ascending" ? a.index - b.index : b.index - a.index;
      }
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }
  return sortableData;
}, [data, sortConfig, currentPage, perPage]);

// Backend search applied; visible data is just the sorted page
const visibleData = useMemo(() => {
  return sortedData;
}, [sortedData]);

useEffect(() => {
  setCurrentPage(0);
}, [searchTerm]);

// Handle search query from global search
useEffect(() => {
  if (searchQuery && searchResultType === 'announcements') {
    setSearchTerm(searchQuery);
    clearSearchQuery();
  }
}, [searchQuery, searchResultType, clearSearchQuery]);

  const getUploadedAttachmentName = (): string => {
    if (!uploadedFile || typeof uploadedFile.get !== 'function') return '';
    const f = uploadedFile.get('files') as File | string | null;
    if (f && typeof f === 'object' && 'name' in f) return String((f as File).name);
    return '';
  };

  const handleAddAnnouncement = () => {
    setShowPublishModal(true);
  }

  const handleModalClose = () => {
    setShowPublishModal(false);
    setAnnouncementMessage("");
    setAnnouncementSubject("");
    setSelectedTenants([]);
    setUploadedFile(null);
    // Clear the file input by changing its key
    setFileInputKey(prev => prev + 1);
  }

  const handleSaveAnnouncement = async () => {
    try {
      setSavingAnnouncement(true);
      const user = await getUserInfo();
      if (!user?.id) return;
      let userId =  user.id || "";
      const response = await createAnnouncement({subject:announcementSubject,message:announcementMessage,tenants:selectedTenants,userId:userId});
      if(response.success){
        if(uploadedFile){
          let formData = new FormData();
          for (let [key, value] of uploadedFile.entries()) {
            formData.append('files', value);
          }
          formData.append('announcementId', response.data.id);
          const response2:any = await uploadFile(formData);
          if(response2.status){
            toast.success(response2.message);
            
          } else {
            toast.error(response2.message);
          }
        }
        toast.success(response.message);
        setAnnouncementMessage("");
        setAnnouncementSubject("");
        setSelectedTenants([]);
        setUploadedFile(null);
        // Clear the file input by changing its key
        setFileInputKey(prev => prev + 1);
        handleModalClose();
        fetchAnnouncementData(currentPage, debouncedSearchTerm, 'table');
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      toast.error("Error creating announcement:", error);
    } finally {
      setSavingAnnouncement(false);
    }
  }

const toggleSort = (key: keyof any | "index") => {
  let direction: "ascending" | "descending" | null = "ascending";
  if (sortConfig.key === key && sortConfig.direction === "ascending") {
    direction = "descending";
  } else if (sortConfig.key === key && sortConfig.direction === "descending") {
    direction = null;
  }
  setSortConfig({ key, direction });
};

// Show initial global loader before deciding on permission
// This ensures the page always starts with the full-screen loader
if (globalLoading) {
  return (
    <div className="loading-screen" 
    style={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                   <div><LoadingScreen/></div>
                 </div>
  );
}

 if (notAllowed) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <div
          className="alert alert-danger text-center"
          style={{
            maxWidth: "500px",
            width: "100%",
            padding: "32px 24px",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            background: "#f8d7da",
            color: "#842029",
            fontSize: "1.2rem",
          }}
        >
          {t("You do not have permission to view this page.")}
        </div>
      </div>
    );
  }

const openFileInNewTab = async (fileUrl: string) => {
  try {
    const response = await authService.apiCall(fileUrl, {
      method: 'GET'
    });

    if (!response.ok) {
      toast("Failed to fetch file.", { type: "error" });
      return;
    }

    const blob = await response.blob();
    const fileObjectURL = URL.createObjectURL(blob);

    const newTab = window.open();
    if (newTab) {
      if (blob.type.startsWith("image/")) {
        newTab.document.write(`<img src="${fileObjectURL}" alt="Secure File" />`);
      } else {
        newTab.document.write(`
          <div style="text-align: center; margin: 20px;">
            <h2 style="color: #333;">${fileUrl.split('/').pop()}</h2>
            <a href="${fileObjectURL}" download target="_blank"
               style="padding: 10px 20px; background-color: orange; color: white; border-radius: 5px; text-decoration: none;">
              Download File
            </a>
          </div>
        `);
      }
    }
  } catch (error: any) {
    toast(error?.message || "Error opening file", { type: "error" });
  }
};
const canCreate = permissionToBool(permissions?.create);
  const pageCount = Math.max(totalPages, 1);

  return (
    <div className="ann-sup-page">
    

      <div className="ann-sup-control-card">
        <div className="ann-sup-toolbar">
          <div className="ann-sup-search-wrap">
            <FiSearch className="ann-sup-search-icon" size={18} aria-hidden />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setCurrentPage(0);
                  fetchAnnouncementData(0, (searchTerm || '').trim(), 'table');
                }
              }}
              placeholder={String(t("announcement.search_placeholder"))}
              className="ann-sup-search-input"
              aria-label={String(t("announcement.search_placeholder"))}
            />
            {searchTerm ? (
              <button type="button" className="ann-sup-clear-search" onClick={() => setSearchTerm('')}>
                {t("announcement.clear_search")}
              </button>
            ) : null}
          </div>
          <div className="ann-sup-actions">
            {canCreate ? (
              <button type="button" className="ann-sup-btn ann-sup-btn-primary" onClick={handleAddAnnouncement}>
                <FaPlus size={14} aria-hidden />
                <span>{t("announcement.add_announcement")}</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

       <div className="container_info ann-sup-table-wrap" >
       
    
        <table className="styled-table">
          <thead>

          
             
            
             



            <tr>
              <th onClick={() => toggleSort("index")} className="sortable-header">
                #
                {sortConfig.key === "index"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("subject")} className="sortable-header">
                {t("announcement.subject")} {" "}
                {sortConfig.key === "subject"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("to")} className="sortable-header">
                {t("announcement.recipients")}{' '}
                {sortConfig.key === "to"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("announcement")} className="sortable-header">
                {t("announcement.announcement")} {" "}
                {sortConfig.key === "announcement"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th>{t('announcement.attachment')}</th>
              <th>{t("announcement.status")}</th>
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="d-flex justify-content-center align-items-center" style={{ padding: '2rem' }}>
                    <div className="spinner-border text-warning" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : visibleData.length > 0 ? (
              visibleData.map((item: any, index: number) => (
                <React.Fragment key={index}>
                  <tr>
                    <td>{
                      sortConfig.key === "index" && sortConfig.direction === "descending"
                        ? currentPage * perPage + (visibleData.length - index)
                        : currentPage * perPage + index + 1
                    }</td>
                    <td>{item.subject}</td>
                    <td>{item.to}</td>
                    <td>{item.message}</td>
                    <td>
                      {item.files?.map((file: any, index: number) => (
                        <FiFile
                          key={index}
                          id={`tooltip-file-${index}`}
                          color="orange"
                          size={20}
                          className="mr-2 cursor-pointer"
                          title={file.name}
                          onClick={() => openFileInNewTab(`${process.env.REACT_APP_API_URL}${file.src}?current=announcement`)}
                        />
                      ))}
                    </td>
                    <td>
                      <span className="badge bg-success-app">
                        {t('announcement.sent')}
                      </span>
                    </td>
                  </tr>
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center alert alert-warning">
                  {t("announcement.no_announcement_available")}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <nav className="ann-sup-pagination" aria-label={String(t('announcement.page'))}>
          <button
            type="button"
            className="ann-sup-page-nav"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}
          >
            ‹ {t('announcement.pagination_previous')}
          </button>
          <span className="ann-sup-page-current" aria-current="page">
            {currentPage + 1}
          </span>
          <button
            type="button"
            className="ann-sup-page-nav"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pageCount - 1))}
            disabled={currentPage >= pageCount - 1}
          >
            {t('announcement.pagination_next')} ›
          </button>
        </nav>
      
      </div>

       <div className="container_info">
              {/* <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
                <h5 style={{width:'100%'}} className="text-center">{t("announcement.not_allowed")}</h5>
              </div> */}
            </div>

            
      <Modal isOpen={showPublishModal} toggle={handleModalClose} centered className="ann-sup-modal">
        <ModalHeader toggle={handleModalClose} className="ann-sup-modal-header">
          {t('announcement.add_announcement')}
        </ModalHeader>
        <ModalBody className="ann-sup-modal-body">
          <div className="ann-sup-modal-field">
            <label className="ann-sup-modal-label" htmlFor="ann-modal-subject">
              {t('announcement.subject')}
            </label>
            <Input
              id="ann-modal-subject"
              type="text"
              placeholder={t('announcement.subject') || ''}
              value={announcementSubject}
              onChange={(e) => setAnnouncementSubject(e.target.value)}
              className="ann-sup-modal-input"
              autoComplete="off"
            />
          </div>
          <div className="ann-sup-modal-field">
            <label className="ann-sup-modal-label" htmlFor="ann-modal-message">
              {t('announcement.announcement')}
            </label>
            <Input
              id="ann-modal-message"
              type="textarea"
              rows={5}
              placeholder={t('announcement.message_placeholder') || ''}
              value={announcementMessage}
              onChange={(e) => setAnnouncementMessage(e.target.value)}
              className="ann-sup-modal-input ann-sup-modal-textarea"
            />
          </div>
          <div className="ann-sup-modal-field">
            <span className="ann-sup-modal-label" id="ann-modal-institutions-label">
              {t('announcement.select_institutions')}
            </span>
            <div className="ann-sup-modal-select">
              <Select
                isMulti
                aria-labelledby="ann-modal-institutions-label"
                inputId="ann-modal-institutions"
                styles={customSelectStyles}
                options={institutions.map((e: any) => ({ value: e.id, label: e.name }))}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(e) => setSelectedTenants(e.map((item: any) => item.value))}
              />
            </div>
          </div>
          <div className="ann-sup-modal-field">
            <span className="ann-sup-modal-label">{t('announcement.modal_attachment')}</span>
            <label className="ann-sup-file-picker" htmlFor="ann-modal-file">
              <span className="ann-sup-file-picker-trigger">{t('announcement.choose_file')}</span>
              <span className="ann-sup-file-picker-name">
                {getUploadedAttachmentName() || t('announcement.no_file_chosen')}
              </span>
              <input
                key={fileInputKey}
                id="ann-modal-file"
                type="file"
                className="ann-sup-file-picker-input"
                onChange={handleFileUpload}
              />
            </label>
            <p className="ann-sup-file-hint">{t('announcement.file_hint')}</p>
          </div>
        </ModalBody>
        <ModalFooter className="ann-sup-modal-footer ann-sup-modal-footer--actions">
          <button type="button" className="ann-sup-modal-btn ann-sup-modal-btn--outline" onClick={handleModalClose}>
            {t('announcement.cancel')}
          </button>
          <LoadingButton
            onClick={handleSaveAnnouncement}
            variant="success"
            className="ann-sup-modal-btn ann-sup-modal-btn--primary"
            disabled={!canCreate || savingAnnouncement}
            loadingText={String(t('announcement.saving') || 'Saving…')}
          >
            {t('announcement.save')}
          </LoadingButton>
        </ModalFooter>
      </Modal>
    </div>
  )
}
